// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { useContext, useRef, useState } from 'react';
import clsx from 'clsx';
import { AppLayoutContext } from './context';
import {
  SplitPanelContext,
  SplitPanelContextProps,
  SplitPanelLastInteraction,
} from '../../internal/context/split-panel-context';
import styles from './styles.css.js';
import { AppLayoutProps } from '../interfaces';
import { useEffectOnUpdate } from '../../internal/hooks/use-effect-on-update';
import { Transition } from '../../internal/components/transition';
import { useObservedElement } from '../utils/use-observed-element';
import customCssProps from '../../internal/generated/custom-css-properties';

/**
 * If there is no Split Panel in the AppLayout context then the SplitPanel
 * will pass through the AppLayout children without the context.
 */
function SplitPanel({ children }: React.PropsWithChildren<unknown>) {
  const {
    handleSplitPanelClick,
    handleSplitPanelPreferencesChange,
    handleSplitPanelResize,
    isMobile,
    isSplitPanelForcedPosition,
    isSplitPanelOpen,
    setSplitPanelReportedSize,
    splitPanelPosition,
    splitPanelSize,
  } = useContext(AppLayoutContext);

  const [openButtonAriaLabel, setOpenButtonAriaLabel] = useState<undefined | string>(undefined);

  const [splitPanelLastInteraction, setSplitPanelLastInteraction] = useState<undefined | SplitPanelLastInteraction>();
  useEffectOnUpdate(
    () => setSplitPanelLastInteraction(isSplitPanelOpen ? { type: 'open' } : { type: 'close' }),
    [isSplitPanelOpen]
  );
  useEffectOnUpdate(() => setSplitPanelLastInteraction({ type: 'position' }), [splitPanelPosition]);

  const context: SplitPanelContextProps = {
    bottomOffset: 0,
    getMaxHeight: () => document.documentElement.clientHeight - 250,
    getMaxWidth: () => document.documentElement.clientWidth,
    isForcedPosition: isSplitPanelForcedPosition,
    isMobile,
    isOpen: isSplitPanelOpen,
    isRefresh: true,
    leftOffset: 0,
    onPreferencesChange: handleSplitPanelPreferencesChange,
    onResize: handleSplitPanelResize,
    onToggle: handleSplitPanelClick,
    position: splitPanelPosition,
    reportSize: setSplitPanelReportedSize,
    rightOffset: 0,
    size: splitPanelSize || 0,
    splitPanelRef: useRef<HTMLDivElement>(null),
    splitPanelHeaderRef: useRef<HTMLDivElement>(null),
    topOffset: 0,
    openButtonAriaLabel,
    setOpenButtonAriaLabel,
    lastInteraction: splitPanelLastInteraction,
  };

  return <SplitPanelContext.Provider value={{ ...context }}>{children}</SplitPanelContext.Provider>;
}

/**
 * This is the render function for the SplitPanel when it is in bottom position.
 * The Split Panel container will be another row entry in the grid definition in
 * the Layout component. The start and finish columns will be variable based
 * on the the presence and state of the Navigation and Tools components.
 */
function SplitPanelBottom() {
  const { disableBodyScroll, isNavigationOpen, isSplitPanelOpen, isToolsOpen, splitPanel, splitPanelReportedSize } =
    useContext(AppLayoutContext);

  const { position: splitPanelPosition, splitPanelHeaderRef } = useContext(SplitPanelContext);

  const headerHeight = useObservedElement(splitPanelHeaderRef);

  if (!splitPanel) {
    return null;
  }

  return (
    <Transition in={isSplitPanelOpen ?? false} exit={false}>
      {(state, transitionEventsRef) => (
        <section
          className={clsx(styles['split-panel-bottom'], state === 'entering' && styles.animating)}
          data-disable-body-scroll={disableBodyScroll ? true : false}
          data-is-navigation-open={isNavigationOpen ? true : false}
          data-is-split-panel-open={isSplitPanelOpen ? true : false}
          data-is-tools-open={isToolsOpen ? true : false}
          data-position={splitPanelPosition}
          ref={transitionEventsRef}
          style={{
            [customCssProps.splitPanelReportedSize]: `${splitPanelReportedSize}px`,
            [customCssProps.splitPanelReportedHeaderSize]: `${headerHeight}px`,
          }}
        >
          <SplitPanel></SplitPanel>
          {splitPanelPosition === 'bottom' && splitPanel}
        </section>
      )}
    </Transition>
  );
}

/**
 * This is the render function for the SplitPanel when it is side position.
 * The Split Panel will not be within the grid defined in the Layout component
 * but instead a direct child of the Tools component. The width constraints
 * for this position are computed in the Tools component.
 */
function SplitPanelSide() {
  const { isSplitPanelOpen, splitPanel, splitPanelMaxWidth, splitPanelMinWidth, splitPanelReportedSize } =
    useContext(AppLayoutContext);

  const { position: splitPanelPosition } = useContext(SplitPanelContext);

  if (!splitPanel) {
    return null;
  }

  return (
    <Transition in={isSplitPanelOpen ?? false} exit={false}>
      {(state, transitionEventsRef) => (
        <section
          aria-hidden={!isSplitPanelOpen || splitPanelPosition === 'bottom' ? true : false}
          className={clsx(styles['split-panel-side'], state === 'entering' && styles.animating)}
          data-is-split-panel-open={isSplitPanelOpen ? true : false}
          data-position={splitPanelPosition}
          ref={transitionEventsRef}
          style={{
            [customCssProps.splitPanelMaxWidth]: `${splitPanelMaxWidth}px`,
            [customCssProps.splitPanelMinWidth]: `${splitPanelMinWidth}px`,
            [customCssProps.splitPanelReportedHeaderSize]: `${splitPanelReportedSize}px`,
          }}
        >
          <div className={clsx(styles['animated-content'])}>{splitPanelPosition === 'side' && splitPanel}</div>
        </section>
      )}
    </Transition>
  );
}

/**
 * Determine the default size value that should be applied to the
 * controlled splitPanelSize property based on the position.
 */
export function getSplitPanelDefaultSize(splitPanelPosition: string) {
  return splitPanelPosition === 'side'
    ? document.documentElement.clientWidth / 3
    : document.documentElement.clientHeight / 2;
}

/**
 * This logic will determine what the Split Panel position should be. It reconciles the possibility
 * of being in forced position with the current selected position in the settings.
 */
export function getSplitPanelPosition(
  isSplitPanelForcedPosition: boolean,
  splitPanelPreferences: AppLayoutProps.SplitPanelPreferences | undefined
) {
  let splitPanelPosition: AppLayoutProps.SplitPanelPosition = 'bottom';

  if (!isSplitPanelForcedPosition && splitPanelPreferences?.position === 'side') {
    splitPanelPosition = 'side';
  }

  return splitPanelPosition;
}
SplitPanel.Bottom = SplitPanelBottom;
SplitPanel.Side = SplitPanelSide;

export default SplitPanel;
